import { useCallback, useEffect, useState } from 'react';
import { useLoadingState } from '../../hooks';
import { LoadingPanel } from '../brand/TwindenLoader';
import { TwindenQr } from '../brand/TwindenQr';
import {
  Box,
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  HStack,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import { Copy, Link2Off, Power, RefreshCw, Share2 } from 'lucide-react';
import { WS_EVENTS } from '@twinden/shared';
import { useWebSocket } from '../../hooks/useWebSocket';
import { api, ApiError, type JoinLink } from '../../lib/api';
import { ConfirmSheet } from '../ConfirmSheet';
import { useT } from '../../i18n';

interface InvitePeopleSheetProps {
  isOpen: boolean;
  onClose: () => void;
  familyId: string;
  familyName: string;
  /** Roots and admins may reset the link or turn it off (D152). */
  canManage: boolean;
}

type Confirm = 'reset' | 'off' | null;

// The family link (D152): every member can share it or show its QR; roots
// and admins can also reset it or turn it off. Joining always needs approval.
export function InvitePeopleSheet({
  isOpen,
  onClose,
  familyId,
  familyName,
  canManage,
}: InvitePeopleSheetProps) {
  const t = useT();
  const toast = useToast();
  const { onEvent } = useWebSocket();
  const phone = useBreakpointValue({ base: true, md: false }) ?? true;
  const [link, setLink] = useState<JoinLink | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const response = await api.getJoinLink(familyId);
      setLink(response.data);
    } catch {
      setFailed(true);
    }
  }, [familyId]);

  useEffect(() => {
    if (isOpen) void load();
  }, [isOpen, load]);
  // The small mark while the link loads; Try again if it fails (M66, D250).
  const linkState = useLoadingState({
    loading: isOpen && !link && !failed,
    hasData: link !== null,
    error: failed,
    retry: () => void load(),
  });

  // Another root or admin reset or turned off the link.
  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (
          isOpen &&
          event === WS_EVENTS.JOIN_LINK_CHANGED &&
          (payload as { familyId?: string }).familyId === familyId
        )
          void load();
      }),
    [onEvent, isOpen, familyId, load]
  );

  const fail = (err: unknown) =>
    toast({
      title: t('account.requests.failed'),
      description:
        err instanceof ApiError ? err.message : t('common.somethingWrong'),
      status: 'error',
    });

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: t('account.invite.copied'), status: 'success' });
    } catch {
      toast({ title: t('account.admin.copyFailed'), status: 'warning' });
    }
  };

  const share = async (url: string) => {
    const text = t('account.invite.shareText', { family: familyName });
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: text, text, url });
        return;
      } catch (err) {
        // Closing the share sheet isn't a failure.
        if ((err as { name?: string })?.name === 'AbortError') return;
      }
    }
    await copy(url);
  };

  const act = async (kind: 'reset' | 'off' | 'on') => {
    setBusy(true);
    try {
      const response =
        kind === 'reset'
          ? await api.resetJoinLink(familyId)
          : await api.setJoinLinkEnabled(familyId, kind === 'on');
      setLink(response.data);
      toast({
        title: t(
          kind === 'reset'
            ? 'account.invite.resetDone'
            : kind === 'off'
              ? 'account.invite.offDone'
              : 'account.invite.onDone'
        ),
        status: 'success',
      });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const title = t('account.invite.title', { family: familyName });

  const content = (
    <Stack spacing={4} pb={2}>
      <Stack spacing="6px" pr={8}>
        <Heading as="h2" fontSize="24px" letterSpacing="-0.01em">
          {title}
        </Heading>
        <Text fontSize="16px" lineHeight="24px" color="ink.body">
          {t('account.invite.body')}
        </Text>
      </Stack>

      {!link || linkState.show ? (
        <LoadingPanel state={linkState} size="inline" py={6} />
      ) : link.enabled && link.url ? (
        <>
          <Center>
            <TwindenQr
              value={link.url}
              label={t('account.invite.qrAlt')}
              size={{ base: '220px', md: '240px' }}
            />
          </Center>
          <Text
            fontSize="13px"
            color="muted"
            textAlign="center"
            wordBreak="break-all"
          >
            {link.url}
          </Text>
          <Stack spacing="10px">
            <Button
              h="56px"
              fontSize="17px"
              leftIcon={<Share2 size={18} />}
              onClick={() => void share(link.url!)}
            >
              {t('account.invite.share')}
            </Button>
            <Button
              h="48px"
              variant="outline"
              colorScheme="gray"
              borderColor="line"
              bg="surface"
              leftIcon={<Copy size={16} />}
              onClick={() => void copy(link.url!)}
            >
              {t('account.invite.copy')}
            </Button>
          </Stack>
          {canManage && (
            <HStack justify="center" spacing={2} pt={1}>
              <Button
                size="sm"
                variant="ghost"
                colorScheme="gray"
                leftIcon={<RefreshCw size={14} />}
                isDisabled={busy}
                onClick={() => setConfirm('reset')}
              >
                {t('account.invite.reset')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                color="danger"
                leftIcon={<Link2Off size={14} />}
                isDisabled={busy}
                onClick={() => setConfirm('off')}
              >
                {t('account.invite.turnOff')}
              </Button>
            </HStack>
          )}
        </>
      ) : (
        <Box bg="surface.subtle" borderRadius="card" p={5} textAlign="center">
          <Text fontWeight={700}>{t('account.invite.off')}</Text>
          {canManage ? (
            <Button
              mt={3}
              leftIcon={<Power size={16} />}
              isLoading={busy}
              onClick={() => void act('on')}
            >
              {t('account.invite.turnOn')}
            </Button>
          ) : (
            <Text fontSize="14px" color="muted" mt={1}>
              {t('account.invite.offMember')}
            </Text>
          )}
        </Box>
      )}
    </Stack>
  );

  return (
    <>
      {phone ? (
        <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
          <DrawerOverlay bg="rgba(31, 29, 27, 0.5)" />
          <DrawerContent
            borderTopRadius="28px"
            bg="surface"
            aria-label={title}
            maxH="92vh"
            pb="max(env(safe-area-inset-bottom), 20px)"
          >
            <DrawerCloseButton top={4} right={4} />
            <DrawerBody px={5} pt="10px" pb={0}>
              <Box
                w="40px"
                h="5px"
                borderRadius="3px"
                bg="gray.200"
                mx="auto"
                mb={4}
                aria-hidden="true"
              />
              {content}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      ) : (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
          <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
          <ModalContent
            mx={5}
            borderRadius="28px"
            bg="surface"
            aria-label={title}
          >
            <ModalCloseButton top={5} right={5} />
            <ModalBody px={6} py={6}>
              {content}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

      <ConfirmSheet
        isOpen={confirm !== null}
        onClose={() => setConfirm(null)}
        icon={
          confirm === 'reset' ? <RefreshCw size={28} /> : <Link2Off size={28} />
        }
        title={t(
          confirm === 'reset'
            ? 'account.invite.resetTitle'
            : 'account.invite.offTitle'
        )}
        body={t(
          confirm === 'reset'
            ? 'account.invite.resetBody'
            : 'account.invite.offBody'
        )}
        confirmLabel={t(
          confirm === 'reset'
            ? 'account.invite.reset'
            : 'account.invite.turnOff'
        )}
        isLoading={busy}
        onConfirm={() => void act(confirm === 'reset' ? 'reset' : 'off')}
      />
    </>
  );
}
